import { FONT_FAMILY } from '../constants/typography';
import { isKitShape, isSketchShape, locksEqualAspect, magneticMinSize, SKETCH_BOX, UML_CLASSIFIER_TYPES } from '../constants/nodeTypes';
import type { Size } from '../types';
import { longestWordWidth, measureWrappedText } from './measureLabel';

export const SKETCH_LABEL = {
  padX: 12,
  padY: 10,
  fontSize: 13,
  lineHeight: 18,
  font: `600 13px ${FONT_FAMILY}`,
} as const;

/** Konva Text `lineHeight` is a multiplier of fontSize. */
export const SKETCH_LABEL_LINE_HEIGHT_RATIO = SKETCH_LABEL.lineHeight / SKETCH_LABEL.fontSize;

export function usesWrappedCenteredLabel(type: string): boolean {
  if (UML_CLASSIFIER_TYPES.has(type) || type === 'uml_actor') return false;
  return isSketchShape(type) || isKitShape(type);
}

/**
 * Fraction of the AABB that stays inside the glyph, so labels are not
 * clipped by diamond/hexagon/oval edges.
 */
export function labelInnerScale(type?: string): { sx: number; sy: number } {
  switch (type) {
    case 'diamond':
    case 'decision':
      return { sx: 0.5, sy: 0.5 };
    case 'hexagon':
      return { sx: 0.72, sy: 0.78 };
    case 'triangle':
      return { sx: 0.55, sy: 0.5 };
    case 'oval':
    case 'net_router':
      return { sx: 0.7, sy: 0.58 };
    case 'cylinder':
    case 'database':
      return { sx: 0.86, sy: 0.7 };
    default:
      return { sx: 1, sy: 1 };
  }
}

export function sketchLabelBox(size: Size, type?: string): { x: number; y: number; width: number; height: number } {
  const { sx, sy } = labelInnerScale(type);
  const padX = sx >= 1 ? SKETCH_LABEL.padX : 2;
  const padY = sy >= 1 ? SKETCH_LABEL.padY : 2;
  const width = Math.max(8, size.width * sx - padX * 2);
  const height = Math.max(8, size.height * sy - padY * 2);
  return {
    x: (size.width - width) / 2,
    y: (size.height - height) / 2,
    width,
    height,
  };
}

export function growSizeToFitLabel(
  text: string,
  size: Size,
  opts: {
    minWidth?: number;
    minHeight?: number;
    padX?: number;
    padY?: number;
    font?: string;
    lineHeight?: number;
    type?: string;
  } = {},
): Size {
  const minWidth = opts.minWidth ?? SKETCH_BOX.MIN_WIDTH;
  const minHeight = opts.minHeight ?? SKETCH_BOX.MIN_HEIGHT;
  const font = opts.font ?? SKETCH_LABEL.font;
  const lineHeight = opts.lineHeight ?? SKETCH_LABEL.lineHeight;
  const { sx, sy } = labelInnerScale(opts.type);
  const padX = opts.padX ?? (sx >= 1 ? SKETCH_LABEL.padX : 2);
  const padY = opts.padY ?? (sy >= 1 ? SKETCH_LABEL.padY : 2);

  const wordW = longestWordWidth(text, font);
  const minOuterW = Math.max(minWidth, size.width, Math.ceil((wordW + padX * 2) / sx));
  const innerW = Math.max(8, minOuterW * sx - padX * 2);
  const wrapped = measureWrappedText(text, innerW, font, lineHeight);
  const minOuterH = Math.max(minHeight, size.height, Math.ceil((wrapped.height + padY * 2) / sy));
  return { width: minOuterW, height: minOuterH };
}

/** Floor size for a resize so wrapped label text still fits inside the glyph. */
export function minSizeForLabel(type: string, text: string): Size {
  const floor = magneticMinSize(type);
  if (!text.trim()) return floor;
  return growSizeToFitLabel(text, floor, {
    type,
    minWidth: floor.width,
    minHeight: floor.height,
  });
}

/**
 * Clamp a drag-resize. Shift/Alt keeps aspect; circle icons stay 1:1;
 * shrinking stops when the label no longer fits.
 */
export function constrainedResize(
  type: string,
  label: string,
  rawW: number,
  rawH: number,
  origin: { w: number; h: number } | null,
  proportional: boolean,
): Size {
  const floor = minSizeForLabel(type, label);
  let newW = Math.max(floor.width, rawW);
  let newH = Math.max(floor.height, rawH);
  const lock = locksEqualAspect(type) || proportional;
  if (lock && origin && origin.w > 0 && origin.h > 0) {
    const scale = Math.max(newW / origin.w, newH / origin.h);
    newW = Math.max(floor.width, Math.round(origin.w * scale));
    newH = Math.max(floor.height, Math.round(origin.h * scale));
  }
  if (locksEqualAspect(type)) {
    const side = Math.max(newW, newH, floor.width, floor.height);
    return { width: side, height: side };
  }
  return { width: newW, height: newH };
}
